import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AUTH, FAILED, IDLE, LOADING, SUCCEEDED, FORGOTPASSWORD, RESETPASSWORD, SIGNIN, SIGNUP } from 'src/common/constants';
import { apiForgetPassword, apiResetPassword, apiSignIn, apiSignUp } from 'src/services/auth.service';

// Define types
interface AuthState {
    user: { email: string } | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    admin: { user: { fName: string, email: string, uuid: string }, roleUser: boolean, token: string } | null;
    isAdmin: boolean,
    isUser: boolean,
    signInRememberMe: boolean,
    signInRememberEmail:string
}

// Define the initial state
const initialState: AuthState = {
    user: null,
    status: IDLE,
    error: null,
    admin: null,
    isAdmin: false,
    isUser:false,
    signInRememberMe: false,
    signInRememberEmail:''
};

// Create async thunks for authentication operations
export const signIn = createAsyncThunk((SIGNIN),
    async (credentials: { email: string; password: string }, { rejectWithValue }) => {
        try {
            const data = await apiSignIn(credentials);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const signUp = createAsyncThunk((SIGNUP),
    async (credentials: { email: string; password: string }, { rejectWithValue }) => {
        try {
            const data = await apiSignUp(credentials);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const resetPassword = createAsyncThunk((RESETPASSWORD),
    async (email: string, { rejectWithValue }) => {
        try {
            const data = await apiResetPassword(email);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const forgetPassword = createAsyncThunk((FORGOTPASSWORD),
    async (email: string, { rejectWithValue }) => {
        try {
            const data = await apiForgetPassword(email);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// Create the slice
const authSlice = createSlice({
    name: AUTH,
    initialState,
    reducers: {
        // Optional synchronous actions can be defined here
        logout: (state) => {
            state.user = null
            state.admin = null
            state.isAdmin = false
            state.isUser = false
            sessionStorage.clear()
            localStorage.clear()
        },
        signInDemo: (state) => {
            state.user = { email: "jun@j.com" }
        },
        admin: (state, { payload }: PayloadAction<any>) => {
            state.admin = payload;
            state.isAdmin = true;
            state.isUser = false;
            state.user = null;
        },
        user: (state, { payload }: PayloadAction<any>) => {
            state.user = payload;
            if (payload.roleUser == true) {
                state.admin = null;
                state.isAdmin = false;
                state.isUser = true;
            }
        },
        signInRememberMeHandler: (state, { payload }: PayloadAction<boolean>) => {
            state.signInRememberMe = payload
        },
        signInRememberEmailHandler: (state, { payload }: PayloadAction<any>) => {
            state.signInRememberEmail = payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(signUp.pending, (state) => {
                state.status = LOADING;
                state.error = null; // Clear the error when the request is pending
            })
            .addCase(signUp.fulfilled, (state, action: PayloadAction<{ email: string }>) => {
                state.status = SUCCEEDED;
                state.user = { email: action.payload.email };
            })
            .addCase(signUp.rejected, (state, action) => {
                state.status = FAILED;
                state.error = action.payload as string; // Use the custom error message
            })
            .addCase(signIn.pending, (state) => {
                state.status = LOADING;
                state.error = null; // Clear the error when the request is pending
            })
            .addCase(signIn.fulfilled, (state, action: PayloadAction<{ email: string }>) => {
                state.status = SUCCEEDED;
                state.user = { email: action.payload.email };
            })
            .addCase(signIn.rejected, (state, action) => {
                state.status = FAILED;
                state.error = action.payload as string; // Use the custom error message
            })
            .addCase(resetPassword.pending, (state) => {
                state.status = LOADING;
                state.error = null; // Clear the error when the request is pending
            })
            .addCase(resetPassword.fulfilled, (state) => {
                state.status = SUCCEEDED;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.status = FAILED;
                state.error = action.payload as string; // Use the custom error message
            })
            .addCase(forgetPassword.pending, (state) => {
                state.status = LOADING;
                state.error = null; // Clear the error when the request is pending
            })
            .addCase(forgetPassword.fulfilled, (state) => {
                state.status = SUCCEEDED;
            })
            .addCase(forgetPassword.rejected, (state, action) => {
                state.status = FAILED;
                state.error = action.payload as string; // Use the custom error message
            });
    },
});

export const { logout, signInDemo,admin,user,signInRememberMeHandler,signInRememberEmailHandler } = authSlice.actions
export default authSlice.reducer;
